import { create } from 'zustand';

export const useBoardStore = create((set, get) => ({
    // ========== Timeline Board ==========
    cards: [],
    lastFetch: {}, // { timelineId: timestamp }
    viewMode: localStorage.getItem('timeline-view-mode') || 'timeline', // 'timeline' | 'workflow'

    // Set all cards (initial fetch)
    setCards: (cards) => set({ cards }),

    // Add a single card (Realtime INSERT or Optimistic Add)
    addCard: (card) => set(state => {
        // Avoid duplicates
        if (state.cards.some(c => c.id === card.id)) return state;
        return { cards: [...state.cards, card] };
    }),

    // Update a card (Realtime UPDATE or Optimistic Update)
    updateCard: (id, updates) => set(state => ({
        cards: state.cards.map(c => c.id === id ? { ...c, ...updates } : c)
    })),

    // Delete a card (Realtime DELETE or Optimistic Delete)
    deleteCard: (id) => set(state => ({
        cards: state.cards.filter(c => c.id !== id)
    })),

    // Check if we should fetch for this timeline
    shouldFetch: (timelineId) => {
        const last = get().lastFetch[timelineId];
        return !last;
    },

    // Mark timeline as fetched
    markFetched: (timelineId) => set(state => ({
        lastFetch: { ...state.lastFetch, [timelineId]: Date.now() }
    })),

    // ========== Demandas Page ==========
    demandsCards: [],
    demandsLastFetch: null,

    setDemandsCards: (cards) => set({ demandsCards: cards }),

    addDemandsCard: (card) => set(state => {
        if (state.demandsCards.some(c => c.id === card.id)) return state;
        return { demandsCards: [...state.demandsCards, card] };
    }),

    updateDemandsCard: (id, updates) => set(state => ({
        demandsCards: state.demandsCards.map(c => c.id === id ? { ...c, ...updates } : c)
    })),

    deleteDemandsCard: (id) => set(state => ({
        demandsCards: state.demandsCards.filter(c => c.id !== id)
    })),

    shouldFetchDemands: () => {
        const last = get().demandsLastFetch;
        return !last || (Date.now() - last > 20000); // 20 second TTL
    },

    markDemandsFetched: () => set({ demandsLastFetch: Date.now() }),

    invalidateDemands: () => set({ demandsLastFetch: null }),

    // ========== Dashboard (Admin) ==========
    users: [],
    timelines: [],
    dashboardLastFetch: null,

    setUsers: (users) => set({ users }),

    setTimelines: (timelines) => set({ timelines }),

    updateUser: (id, updates) => set(state => ({
        users: state.users.map(u => u.id === id ? { ...u, ...updates } : u)
    })),

    addUser: (user) => set(state => ({ users: [...state.users, user] })),

    deleteUser: (id) => set(state => ({
        users: state.users.filter(u => u.id !== id)
    })),

    updateTimeline: (id, updates) => set(state => ({
        timelines: state.timelines.map(t => t.id === id ? { ...t, ...updates } : t)
    })),

    addTimeline: (timeline) => set(state => ({ timelines: [...state.timelines, timeline] })),

    deleteTimeline: (id) => set(state => ({
        timelines: state.timelines.filter(t => t.id !== id)
    })),

    shouldFetchDashboard: () => {
        const last = get().dashboardLastFetch;
        return !last || (Date.now() - last > 20000); // 20 second TTL
    },

    markDashboardFetched: () => set({ dashboardLastFetch: Date.now() }),

    invalidateDashboard: () => set({ dashboardLastFetch: null }),

    // ========== View Mode ==========
    setViewMode: (viewMode) => {
        localStorage.setItem('timeline-view-mode', viewMode);
        set({ viewMode });
    },

    // ========== Global Cache Clear ==========
    clearCache: () => set({
        cards: [],
        lastFetch: {},
        viewMode: 'timeline',
        demandsCards: [],
        demandsLastFetch: null,
        users: [],
        timelines: [],
        dashboardLastFetch: null
    })
}));
